import { useState, useEffect, useCallback, useRef } from 'react';
import {
  COLUMN_WIDTHS_STORAGE_KEY,
  COLUMN_WIDTH_WEIGHTS,
  RESIZABLE_COLUMN_KEYS,
  getMinColumnWidth,
} from '@/components/timesheet/timesheetGridUtils';

export interface UseColumnResizeOptions {
  /** Scroll container the table is laid out in. */
  tableScrollRef: React.RefObject<HTMLDivElement | null>;
  /** Changes whenever the set or size of the non-resizable columns changes. */
  layoutKey: string;
}

export interface UseColumnResizeResult {
  /** Width to render each column at: the user's width, or its share of the free width. */
  columnWidths: Record<string, number>;
  handleResizeStart: (e: React.MouseEvent, columnKey: string) => void;
  registerColumnHeader: (columnKey: string) => (el: HTMLTableCellElement | null) => void;
  headerRowRef: React.RefObject<HTMLTableRowElement | null>;
  spacerHeaderRef: React.RefObject<HTMLTableCellElement | null>;
}

/**
 * Manages the resizable non-hours columns of the timesheet grid: the persisted
 * column widths, the drag (mousedown/move/up) lifecycle, and localStorage
 * persistence. Widths are persisted so the layout survives reloads.
 *
 * Only columns the user has actually resized get a persisted pixel width; the rest
 * share whatever width the fixed-width day/Total/Actions columns leave over, so an
 * untouched grid fills its container instead of stopping short at default widths.
 */
export function useColumnResize({ tableScrollRef, layoutKey }: UseColumnResizeOptions): UseColumnResizeResult {
  const [pinnedWidths, setPinnedWidths] = useState<Record<string, number>>(() => {
    try {
      const stored = localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, number>;
        return Object.fromEntries(
          Object.entries(parsed).map(([key, width]) => [key, Math.max(getMinColumnWidth(key), width)])
        );
      }
    } catch {
      // Ignore malformed stored widths and let every column size itself
    }
    return {};
  });
  const [sharedWidths, setSharedWidths] = useState<Record<string, number>>({});
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  const columnHeaderRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const spacerHeaderRef = useRef<HTMLTableCellElement>(null);

  const registerColumnHeader = useCallback(
    (columnKey: string) => (el: HTMLTableCellElement | null) => {
      if (el) {
        columnHeaderRefs.current[columnKey] = el;
      } else {
        delete columnHeaderRefs.current[columnKey];
      }
    },
    []
  );

  const handleResizeStart = useCallback((e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    // Pin every column at its rendered width first, so the drag neither makes the dragged
    // column jump nor reflows the columns that are still sharing the free width.
    const measuredWidths: Record<string, number> = {};
    for (const key of RESIZABLE_COLUMN_KEYS) {
      const header = columnHeaderRefs.current[key];
      if (header) {
        measuredWidths[key] = Math.max(getMinColumnWidth(key), header.getBoundingClientRect().width);
      }
    }
    setPinnedWidths(prev => ({ ...measuredWidths, ...prev }));
    setResizingColumn(columnKey);
    setResizeStartX(e.clientX);
    setResizeStartWidth(pinnedWidths[columnKey] ?? measuredWidths[columnKey] ?? getMinColumnWidth(columnKey));
  }, [pinnedWidths]);

  useEffect(() => {
    if (!resizingColumn) return;

    const handleResizeMove = (e: MouseEvent) => {
      const diff = e.clientX - resizeStartX;
      const newWidth = Math.max(getMinColumnWidth(resizingColumn), resizeStartWidth + diff);
      setPinnedWidths(prev => ({ ...prev, [resizingColumn]: newWidth }));
    };
    const handleResizeEnd = () => setResizingColumn(null);

    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  useEffect(() => {
    if (Object.keys(pinnedWidths).length === 0) return;
    try {
      localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(pinnedWidths));
    } catch {
      // Ignore quota/serialization errors — column widths are non-critical
    }
  }, [pinnedWidths]);

  // Share the width left over by the fixed-width day/Total/Actions columns between the
  // columns the user has not resized.
  useEffect(() => {
    const wrapper = tableScrollRef.current;
    if (!wrapper) return;

    const measure = () => {
      const headerRow = headerRowRef.current;
      if (!headerRow) return;

      const renderedKeys = RESIZABLE_COLUMN_KEYS.filter(key => columnHeaderRefs.current[key]);
      const flexibleKeys = renderedKeys.filter(key => pinnedWidths[key] == null);
      if (flexibleKeys.length === 0) {
        setSharedWidths(prev => (Object.keys(prev).length === 0 ? prev : {}));
        return;
      }

      // The spacer only holds whatever the other columns leave behind, so it must not
      // count towards the width that is already spoken for.
      const excludedCells = new Set<Element | null>(renderedKeys.map(key => columnHeaderRefs.current[key]));
      excludedCells.add(spacerHeaderRef.current);
      let otherColumnsWidth = 0;
      let allColumnsWidth = 0;
      for (const cell of headerRow.children) {
        const cellWidth = cell.getBoundingClientRect().width;
        allColumnsWidth += cellWidth;
        if (!excludedCells.has(cell)) {
          // Some fixed columns (Actions) render a label wider than the column itself, so
          // leave room for it rather than handing that overflow a horizontal scrollbar.
          otherColumnsWidth += Math.max(cellWidth, cell.scrollWidth);
        }
      }
      // Collapsed table borders sit outside the cells, so the table is a little wider
      // than the sum of its columns.
      const borderOverhead = Math.max(
        0,
        (headerRow.closest('table')?.getBoundingClientRect().width ?? 0) - allColumnsWidth
      );
      const pinnedWidth = renderedKeys.reduce((total, key) => total + (pinnedWidths[key] ?? 0), 0);
      let freeWidth = wrapper.clientWidth - otherColumnsWidth - pinnedWidth - borderOverhead;

      // Share the free width by weight, pinning any column whose share falls below its
      // minimum and re-sharing what is left over the rest. Once every column is at its
      // minimum the table simply overflows and scrolls horizontally.
      const next: Record<string, number> = {};
      let pending = [...flexibleKeys];
      while (pending.length > 0) {
        const totalWeight = pending.reduce((total, key) => total + (COLUMN_WIDTH_WEIGHTS[key] ?? 1), 0);
        const belowMinimum = pending.find(
          key => (freeWidth * (COLUMN_WIDTH_WEIGHTS[key] ?? 1)) / totalWeight < getMinColumnWidth(key)
        );
        if (!belowMinimum) {
          for (const key of pending) {
            next[key] = (freeWidth * (COLUMN_WIDTH_WEIGHTS[key] ?? 1)) / totalWeight;
          }
          break;
        }
        next[belowMinimum] = getMinColumnWidth(belowMinimum);
        freeWidth -= next[belowMinimum];
        pending = pending.filter(key => key !== belowMinimum);
      }
      setSharedWidths(prev => {
        const unchanged =
          Object.keys(prev).length === Object.keys(next).length &&
          Object.entries(next).every(([key, width]) => Math.abs((prev[key] ?? 0) - width) < 0.5);
        return unchanged ? prev : next;
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [pinnedWidths, layoutKey, tableScrollRef]);

  return {
    columnWidths: { ...sharedWidths, ...pinnedWidths },
    handleResizeStart,
    registerColumnHeader,
    headerRowRef,
    spacerHeaderRef,
  };
}
